/**
 * Version utility for Web app
 * Provides version information and logging
 */

interface VersionInfo {
  version: string;
  buildDate: string | null;
  platform: string;
}

let cachedVersion: VersionInfo | null = null;
let versionLoadPromise: Promise<VersionInfo> | null = null;

/**
 * Get version information from version.json
 * Works both at build time (via require) and at runtime (via fetch from public folder)
 */
export const getVersionInfo = (): VersionInfo => {
  if (cachedVersion) {
    return cachedVersion;
  }

  // If we're in the browser and haven't loaded yet, we need to fetch it
  // But since this is a synchronous function, we'll use a default and load async
  if (typeof window !== 'undefined' && !versionLoadPromise) {
    // Start async load but return default for now
    loadVersionAsync();
    // Return a default version to prevent errors
    const envVersion = process.env.REACT_APP_VERSION || '3.2.0';
    cachedVersion = {
      version: envVersion,
      buildDate: process.env.REACT_APP_BUILD_DATE || null,
      platform: 'web',
    };
    return cachedVersion;
  }

  // Use environment variable as build-time fallback, fetch from /version.json at runtime
  const envVersion = process.env.REACT_APP_VERSION;
  if (envVersion) {
    console.log('[Version] Using version from environment variable');
    cachedVersion = {
      version: envVersion,
      buildDate: process.env.REACT_APP_BUILD_DATE || null,
      platform: 'web',
    };
    return cachedVersion;
  }
  
  // Default version if no environment variable is set
  // Will be updated when fetch from /version.json completes
  const defaultVersion = '3.2.0';
  cachedVersion = {
    version: defaultVersion,
    buildDate: process.env.REACT_APP_BUILD_DATE || null,
    platform: 'web',
  };
  
  console.log(`[Version] Using default version: ${defaultVersion} (will fetch from /version.json at runtime)`);
  return cachedVersion;
};

/**
 * Load version.json asynchronously from public folder (for production runtime)
 */
async function loadVersionAsync(): Promise<VersionInfo> {
  if (versionLoadPromise) {
    return await versionLoadPromise;
  }

  versionLoadPromise = (async (): Promise<VersionInfo> => {
    try {
      const response = await fetch('/version.json');
      if (response.ok) {
        const versionModule = await response.json();
        
        if (versionModule?.platforms && versionModule.platforms.web) {
          const version = versionModule.platforms.web;
          const buildDate = versionModule?.buildDate || process.env.REACT_APP_BUILD_DATE || null;
          
          cachedVersion = {
            version,
            buildDate,
            platform: 'web',
          };
          
          console.log(`[Version] Loaded from /version.json at runtime: ${version}`);
          return cachedVersion;
        }
      }
    } catch (e) {
      console.warn('[Version] Failed to fetch /version.json, using cached/default version');
    }
    
    // Fallback to environment variable
    const envVersion = process.env.REACT_APP_VERSION;
    if (envVersion) {
      cachedVersion = {
        version: envVersion,
        buildDate: process.env.REACT_APP_BUILD_DATE || null,
        platform: 'web',
      };
      return cachedVersion;
    }
    
    // Final fallback
    if (!cachedVersion) {
      cachedVersion = {
        version: '3.2.0',
        buildDate: process.env.REACT_APP_BUILD_DATE || null,
        platform: 'web',
      };
    }
    
    return cachedVersion;
  })();

  return await versionLoadPromise;
}

/**
 * Log version information to console
 * Attempts to load version asynchronously if not already cached
 */
export const logVersion = async () => {
  // Try to load version async if in browser
  if (typeof window !== 'undefined') {
    try {
      await loadVersionAsync();
    } catch (e) {
      // Ignore errors, use cached version
    }
  }
  
  const versionInfo = getVersionInfo();
  
  const versionString = `AllAvail Web v${versionInfo.version}${versionInfo.buildDate ? ` - Built ${versionInfo.buildDate}` : ''}`;
  
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('🌐', versionString);
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
};

/**
 * Get version string for display
 */
export const getVersionString = (): string => {
  const versionInfo = getVersionInfo();
  return `v${versionInfo.version}`;
};

